import React, { useEffect } from 'react';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import {
  STATEMENT_PSEUDO_ELEMENT_ID_PREFIX,
  getAncestorElement,
} from 'constants/feature/statement-content-constants';
import {
  getNoteStatusIcon,
  getNoteStatusColor,
} from 'constants/feature/notes-panel-constants';
import Note from 'models/data/note-model';

import AnnotationDisplayOptions from 'models/data/annotation-display-filter-options-model';
import { isElementReportPreviewPage } from 'utils/ocr-annotation-utils';
import { createRoot } from 'react-dom/client';

export const NOTE_ANCHOR_BLOCK = 'note-anchor';
const ICON_SIZE = '20';
const BORDER_THICKNESS = '1px';
const LEFT_SIDE_CLASS = 'left';
const MAIN_SIDE_CLASS = 'main';

// we are creating this component so that react-dom is able to get latest icon
// as this component will re-render whenever the props change
export const NoteIcon = ({ note, iconProps, notesCursor }) => {
  return <span>{getNoteStatusIcon({ note, iconProps, notesCursor })}</span>;
};

const handleHighlightNote = ({
  className,
  isNoteNew,
  onHover,
  panelSelected,
  changeIsNewPropertyFromSelectedNote,
  leftSideView,
}) => {
  // if a note is created that spans multiple html elements, each of those elements
  // ends up with a pseudo note span.CFTO_PSEUDO_ELEMENT_<pseudoElementId>.CFTO_PSEUDO_ELEMENT_<pseudoElementId>_<spanIncrement>
  // first we find all pseudo note parts for this specific note
  const pseudoNoteIdClass = String(className)
    .split(' ')
    .find((c) => c.includes(STATEMENT_PSEUDO_ELEMENT_ID_PREFIX));
  const allNoteParts = document.querySelectorAll(
    `.${pseudoNoteIdClass}-${leftSideView ? LEFT_SIDE_CLASS : MAIN_SIDE_CLASS}`,
  );
  if (allNoteParts.length > 0) {
    for (let i = 0; i < allNoteParts.length; i++) {
      allNoteParts[i].classList.add(
        panelSelected ? 'panel-selected' : 'highlight',
      );
      if (onHover) {
        allNoteParts[i].classList.add('hover-highlight');
      } else {
        allNoteParts[i].classList.remove('hover-highlight');
        allNoteParts[i].classList.add('highlight');
      }
      if (isNoteNew) {
        allNoteParts[i].classList.add('created-note');
        document.addEventListener(
          'click',
          (e) => {
            if (!e.ctrlKey) {
              allNoteParts[i].classList.remove('created-note');
              if (typeof changeIsNewPropertyFromSelectedNote === 'function') {
                // set the isNew property of the selected Note as false when user clicks away
                changeIsNewPropertyFromSelectedNote(false);
              }
            }
          },
          {
            once: true,
          },
        );
      }
    }
  }
};

const createAndAttachNoteIcon = ({
  pseudoElementId,
  note,
  iconProps,
  notesCursor,
  lastNotePart,
  leftSideView,
  existingIconContainer,
  noteIcons,
}) => {
  if (existingIconContainer) {
    for (let i = 0; i < noteIcons.length; i++) {
      if (noteIcons[i].parentNode) {
        noteIcons[i].remove();
      }
    }
  }
  const iconContainer = document.createElement('div');
  iconContainer.className = `note-icon-container-${pseudoElementId}-${
    leftSideView ? LEFT_SIDE_CLASS : MAIN_SIDE_CLASS
  }`; // Add a class to identify the icon container
  iconContainer.style.display = 'inline';

  // Render the React element into the container
  const root = createRoot(iconContainer);
  root.render(
    <NoteIcon note={note} iconProps={iconProps} notesCursor={notesCursor} />,
  );

  if (iconContainer && lastNotePart) {
    // Insert the iconContainer after the lastNotePart
    lastNotePart.parentNode.insertBefore(
      iconContainer,
      lastNotePart.nextSibling,
    );
  }
};

const attachNotesIconAtLastNode = ({
  note,
  iconProps,
  notesCursor,
  className,
  displayElementDetailsOnStatementPage,
  annotationDisplayOptions,
  pseudoElementId,
  leftSideView,
}) => {
  const pseudoNoteIdClass = String(className)
    .split(' ')
    .find((c) => c.includes(STATEMENT_PSEUDO_ELEMENT_ID_PREFIX));
  const allNoteParts = document.querySelectorAll(
    `.${pseudoNoteIdClass}-${leftSideView ? LEFT_SIDE_CLASS : MAIN_SIDE_CLASS}`,
  );

  if (allNoteParts.length > 0) {
    for (let i = 0; i < allNoteParts.length; i++) {
      const notePart = allNoteParts[i];
      // Remove any existing borders
      notePart.style.borderLeft = 'none';
      notePart.style.borderRight = 'none';
      notePart.style.borderTop = 'none';
      notePart.style.borderBottom = 'none';

      // Check the conditions to apply borders
      if (
        displayElementDetailsOnStatementPage &&
        annotationDisplayOptions.note
      ) {
        const borderColor = getNoteStatusColor({ note });
        notePart.style.borderTop = `${BORDER_THICKNESS} solid ${borderColor}`;
        notePart.style.borderBottom = `${BORDER_THICKNESS} solid ${borderColor}`;

        // Apply left border for the first notePart
        if (i === 0) {
          notePart.style.borderLeft = `${BORDER_THICKNESS} solid ${borderColor}`;
        }

        // Apply right border for the last notePart
        if (i === allNoteParts.length - 1) {
          notePart.style.borderRight = `${BORDER_THICKNESS} solid ${borderColor}`;
        }
      }
    }

    const FIND_PARENT_ELEMENT_UPTO_LEVEL = 5;
    const lastNotePart =
      getAncestorElement(
        allNoteParts[allNoteParts.length - 1],
        FIND_PARENT_ELEMENT_UPTO_LEVEL,
        'a.tieout-element',
      ) || allNoteParts[allNoteParts.length - 1];

    const noteIcons = document.getElementsByClassName(
      `note-icon-container-${pseudoElementId}-${
        leftSideView ? LEFT_SIDE_CLASS : MAIN_SIDE_CLASS
      }`,
    );
    const existingIconContainer = !!noteIcons.length;

    // Remove and Display exisiting icon/icons based on conditions
    if (
      !displayElementDetailsOnStatementPage ||
      !annotationDisplayOptions.note
    ) {
      if (existingIconContainer) {
        for (let i = 0; i < noteIcons.length; i++) {
          noteIcons[i].style.display = 'none';
        }
      }
    } else {
      if (existingIconContainer) {
        for (let i = 0; i < noteIcons.length; i++) {
          noteIcons[i].style.display = 'inline';
        }
      }
    }

    if (displayElementDetailsOnStatementPage && annotationDisplayOptions.note) {
      createAndAttachNoteIcon({
        pseudoElementId,
        note,
        iconProps,
        notesCursor,
        lastNotePart,
        leftSideView,
        existingIconContainer,
        noteIcons,
      });
    }
  }
};
// clear highlight when the user is not hovering over note icon
const handleRemoveNoteHighlight = (options = {}) => {
  const { className, isNoteSelected, leftSideView } = options;
  let querySelector = '';
  if (className) {
    querySelector =
      String(className)
        .split(' ')
        .find((c) => c.includes(STATEMENT_PSEUDO_ELEMENT_ID_PREFIX)) +
      `-${leftSideView ? LEFT_SIDE_CLASS : MAIN_SIDE_CLASS}`;
  } else {
    querySelector = 'highlight';
  }
  const allNoteParts = document.querySelectorAll(`.${querySelector}`);
  if (allNoteParts.length > 0) {
    for (let i = 0; i < allNoteParts.length; i++) {
      if (!isNoteSelected) {
        allNoteParts[i].classList.remove('highlight');
        allNoteParts[i].classList.remove('created-note');
        allNoteParts[i].classList.remove('hover-highlight');
        allNoteParts[i].classList.remove('panel-selected');
      } else {
        allNoteParts[i].classList.remove('hover-highlight');
        allNoteParts[i].classList.remove('highlight');
      }
    }
  }
};

const NoteAnchor = ({
  id,
  onNoteClick,
  children,
  className,
  pseudoElementId,
  note,
  isNoteSelected,
  selectMode,
  isNoteNew,
  panelSelected,
  isNotesPanelOpen,
  changeIsNewPropertyFromSelectedNote,
  annotationDisplayOptions,
  handleNoteClick,
  isCursorAlias,
  shouldNotesDisabled,
  displayElementDetailsOnStatementPage,
  leftSideView,
}) => {
  useEffect(() => {
    if (isNoteSelected) {
      handleHighlightNote({
        className,
        isNoteNew,
        panelSelected,
        changeIsNewPropertyFromSelectedNote,
        leftSideView,
      });
    } else {
      handleRemoveNoteHighlight({ className, leftSideView });
    }
  }, [
    changeIsNewPropertyFromSelectedNote,
    className,
    isNoteNew,
    isNoteSelected,
    panelSelected,
    isNotesPanelOpen,
    leftSideView,
  ]);
  useEffect(() => {
    if (!isNotesPanelOpen) {
      handleRemoveNoteHighlight({ className, leftSideView });
    }
  }, [className, isNotesPanelOpen, leftSideView]);

  const iconProps = {
    className: `${NOTE_ANCHOR_BLOCK}__icon`,
    onClick: (event) => {
      handleNoteClick(event, selectMode, note, pseudoElementId, onNoteClick);
    },
    onMouseEnter: () => {
      handleHighlightNote({ className, onHover: true, leftSideView });
    },
    onMouseLeave: () => {
      handleRemoveNoteHighlight({ className, isNoteSelected, leftSideView });
    },
    height: ICON_SIZE,
    width: ICON_SIZE,
  };

  const notesCursor = {
    className: classnames(
      isCursorAlias && `${NOTE_ANCHOR_BLOCK}--cursor-alias`,
      shouldNotesDisabled && `${NOTE_ANCHOR_BLOCK}--disabled`,
      `${NOTE_ANCHOR_BLOCK}__icon`,
    ),
    closedPriorityClassName: `${NOTE_ANCHOR_BLOCK}__closed-priority`,
  };

  useEffect(() => {
    attachNotesIconAtLastNode({
      note,
      iconProps,
      notesCursor,
      className,
      displayElementDetailsOnStatementPage,
      annotationDisplayOptions,
      pseudoElementId,
      leftSideView,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    pseudoElementId,
    note,
    iconProps,
    notesCursor,
    displayElementDetailsOnStatementPage,
    annotationDisplayOptions.note,
    className,
    isNoteSelected,
    isNotesPanelOpen,
    changeIsNewPropertyFromSelectedNote,
    isNoteNew,
    panelSelected,
    leftSideView,
  ]);

  const getClassName = (className) => {
    const pseudoNoteIdClass = String(className)
      .split(' ')
      .find((c) => c.includes(STATEMENT_PSEUDO_ELEMENT_ID_PREFIX));

    return leftSideView
      ? `${pseudoNoteIdClass}-${LEFT_SIDE_CLASS}`
      : `${pseudoNoteIdClass}-${MAIN_SIDE_CLASS}`;
  };

  return !isElementReportPreviewPage(window.location.pathname) ? (
    <>
      {/*note anchor as sibling of content allows for easy icon hover styling */}
      <span
        id={
          id
            ? id
            : className &&
              !!className.split(' ').length &&
              className.split(' ')[0]
        }
      >
        {
          <span
            className={classnames(
              `${NOTE_ANCHOR_BLOCK}__content`,
              className,
              `${getClassName(className)}`,
            )}
          >
            {children}
          </span>
        }
      </span>
    </>
  ) : (
    <>{children}</>
  );
};

NoteAnchor.propTypes = {
  /** unique html id for this note anchor (should be CFTO_PSEUDO_ELEMENT_XXXXXXX) */
  id: PropTypes.string.isRequired,
  /** function fired on click of this note anchor */
  onNoteClick: PropTypes.func.isRequired,
  /** Optional classname applied to top level wrapper element */
  className: PropTypes.string,
  /** Data model for this note */
  note: PropTypes.instanceOf(Note).isRequired,
  /** Indicates if note is selected */
  isNoteSelected: PropTypes.bool.isRequired,
  /** string value to determine which select mode is activated */
  selectMode: PropTypes.string,
  /* bool that indicates if a note is new */
  isNoteNew: PropTypes.bool,
  /* action to manually change the isNew property of the selected note */
  changeIsNewPropertyFromSelectedNote: PropTypes.func.isRequired,
  /** Obj containing display options from store */
  annotationDisplayOptions: PropTypes.instanceOf(AnnotationDisplayOptions),
  /** callback  function which is used to handles the notes api call */
  handleNoteClick: PropTypes.func,
};

export default NoteAnchor;
